import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { passwordService } from '@/services/password.service';
import { PasswordStrengthIndicator } from '@/components/auth/PasswordStrengthIndicator';
import { generateRequestId, encryptAES } from '@/utils/crypto';
import toast from 'react-hot-toast';
import useApplyUITheme, { useSiteTheme } from '@/hooks/useApplyUITheme';

import {
  Eye,
  EyeOff,
  Lock,
  ArrowRight,
  AlertCircle,
  CheckCircle,
  Clock,
} from 'lucide-react';

const resetPasswordSchema = yup.object().shape({
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/\d/, 'Password must contain at least one number')
    .required('Password is required'),
  confirm_password: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
});

type ResetPasswordFormData = {
  password: string;
  confirm_password: string;
};

export default function ResetPassword() {
  useApplyUITheme('auth');
  const { saved } = useSiteTheme('auth');
  const primaryColor = saved?.primary_color;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<
    'weak' | 'medium' | 'strong' | 'very_strong'
  >('weak');
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenValidating, setTokenValidating] = useState(true);
  const [tokenError, setTokenError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [tokenExpiresIn, setTokenExpiresIn] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({
    resolver: yupResolver(resetPasswordSchema),
  });

  const password = watch('password');

  // Validate reset token on mount
  useEffect(() => {
    if (!resetToken) {
      setTokenError(
        'No reset token provided. Please use the link from your email.',
      );
      setTokenValidating(false);
      return;
    }

    const validateToken = async () => {
      try {
        const result = await passwordService.validateResetToken(resetToken);
        if (result.valid) {
          setTokenValid(true);
          setTokenEmail(result.email || '');
          setTokenExpiresIn(result.expiresIn || null);
        } else {
          setTokenError('This password reset link is invalid or has expired.');
          setTokenValid(false);
        }
      } catch (error: any) {
        const errorMessage = error.message.toLowerCase();
        if (errorMessage.includes('expired')) {
          setTokenError(
            'This password reset link has expired. Please request a new one.',
          );
        } else {
          setTokenError(
            'This password reset link is invalid. Please request a new one.',
          );
        }
        setTokenValid(false);
      } finally {
        setTokenValidating(false);
      }
    };

    validateToken();
  }, [resetToken]);

  const resetPasswordMutation = useMutation({
    mutationFn: (data: ResetPasswordFormData) => {
      const requestId = generateRequestId();
      const encryptedPassword = encryptAES(data.password, requestId);
      const encryptedConfirmPassword = encryptAES(
        data.confirm_password,
        requestId,
      );
      return passwordService.resetPassword(
        resetToken!,
        encryptedPassword,
        encryptedConfirmPassword,
        requestId,
      );
    },
    onSuccess: () => {
      setResetSuccess(true);
      toast.success('Password reset successful!');
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    },
    onError: (error: Error) => {
      const errorMessage = error.message.toLowerCase();

      if (errorMessage.includes('expired')) {
        setTokenError(
          'This password reset link has expired. Please request a new one.',
        );
        setTokenValid(false);
      } else if (errorMessage.includes('same')) {
        toast.error(
          'New password cannot be the same as your current password.',
        );
      } else {
        toast.error(error.message);
      }
    },
  });

  const onSubmit = (data: ResetPasswordFormData) => {
    if (passwordStrength === 'weak') {
      toast.error('Please choose a stronger password');
      return;
    }

    resetPasswordMutation.mutate(data);
  };

  // Loading state
  if (tokenValidating) {
    return (
      <div className="w-full flex flex-col items-center justify-center py-12">
        <div
          className={`w-12 h-12 border-4 rounded-full animate-spin mb-4 ${
            !primaryColor ? 'border-primary-200 border-t-primary-600' : ''
          }`}
          style={
            primaryColor
              ? {
                  borderColor: `${primaryColor}33`,
                  borderTopColor: primaryColor,
                }
              : undefined
          }
        />
        <p className="text-gray-600">Validating reset link...</p>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="w-full">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900">
            Invalid Reset Link
          </h2>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <p className="text-sm text-red-800 mb-3">{tokenError}</p>
              <Link
                to="/forgot-password"
                className={`text-sm font-medium ${
                  !primaryColor ? 'text-primary-700 hover:text-primary-800' : ''
                }`}
                style={primaryColor ? { color: primaryColor } : undefined}
              >
                Request a new password reset link
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (resetSuccess) {
    return (
      <div className="w-full">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900">
            Password Reset Successful!
          </h2>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <p className="text-sm text-green-800 mb-3">
                Your password has been successfully reset. You can now log in
                with your new password.
              </p>
              <p className="text-sm text-gray-600">
                Redirecting to login page...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900">
          Reset Your Password
        </h2>
        <p className="text-gray-600 mt-2">
          Create a new password for your account
        </p>
      </div>

      {/* Token info */}
      {tokenEmail && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm">
              <p className="text-blue-900">
                Resetting password for: <strong>{tokenEmail}</strong>
              </p>
              {tokenExpiresIn && tokenExpiresIn > 0 && (
                <p className="text-blue-700 mt-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Link expires in {Math.floor(tokenExpiresIn / 60)} minutes
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* New Password Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            New Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              className="input-field pl-10 pr-10"
              placeholder="Enter your new password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showPassword ? (
                <Eye className="w-5 h-5" />
              ) : (
                <EyeOff className="w-5 h-5" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-red-500 text-xs mt-1">
              {errors.password.message}
            </p>
          )}
          <PasswordStrengthIndicator
            password={password || ''}
            onStrengthChange={setPasswordStrength}
          />
        </div>

        {/* Confirm Password Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Confirm New Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              {...register('confirm_password')}
              type={showConfirmPassword ? 'text' : 'password'}
              className="input-field pl-10 pr-10"
              placeholder="Re-enter your new password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showConfirmPassword ? (
                <Eye className="w-5 h-5" />
              ) : (
                <EyeOff className="w-5 h-5" />
              )}
            </button>
          </div>
          {errors.confirm_password && (
            <p className="text-red-500 text-xs mt-1">
              {errors.confirm_password.message}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={
            isSubmitting ||
            resetPasswordMutation.isPending ||
            passwordStrength === 'weak'
          }
          className="btn-primary w-full flex items-center justify-center gap-2"
          style={primaryColor ? { backgroundColor: primaryColor } : undefined}
        >
          {isSubmitting || resetPasswordMutation.isPending ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Resetting Password...
            </>
          ) : (
            <>
              Reset Password
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>

        <div className="text-center text-sm text-gray-600">
          Remember your password?{' '}
          <Link
            to="/login"
            className={`font-medium ${
              !primaryColor ? 'text-primary-700 hover:text-primary-800' : ''
            }`}
            style={primaryColor ? { color: primaryColor } : undefined}
          >
            Sign in here
          </Link>
        </div>
      </form>
    </div>
  );
}
